/**
 * CC-1a — telemetryServerChannel.
 *
 * # Why this exists (ТЗ §14, sub-telemetry regression anchor)
 *
 * ТЗ §14 requires command tracking — clicks, commands, params, errors and
 * timings — to be forwarded to a SERVER-SIDE channel with aggregation.
 * Before this slice the editor only had:
 *   - `pushAuditEvent` (editorStore.ts ~1776) — an IN-MEMORY audit ring
 *     capped at the newest 24/48 events. No params, no duration, no error
 *     status, NO server delivery.
 *   - `rendererTelemetry.ts` — a single boot-time counter, fire-and-forget
 *     (console + CustomEvent). Lossy by design.
 *
 * This module is the missing transport: a typed, batched, retrying server
 * channel. It does NOT replace the audit ring — it OBSERVES it and projects
 * every emitted audit event into the CAD-core `TelemetryEvent` model
 * (`@dxf-ai-editor/contracts`), then batches + POSTs to the Laravel ingest
 * endpoint with backpressure + retry so events are never silently lost.
 *
 * # Why hook the audit ring (not geometryShellReducer)
 *
 * `pushAuditEvent` is the single chokepoint every audited command flows
 * through — the 6 in-memory-emitting telemetry axes (cmd-dim, cmd-extend,
 * sub-mlv, sub-grips, sub-ai-draft, sub-telemetry) AND a future REGEN
 * view-op audit event. REGEN does NOT pass through `geometryShellReducer`
 * (it mutates no geometry), so hooking the reducer would miss it. Observing
 * the audit ring transports all of them, REGEN included (CC-1a A1).
 *
 * # Scope (CC-1a only)
 *
 *   - cmd-zoom emitter is NOT added here (it writes a transient
 *     `appendCommandFeed`, not `pushAuditEvent`; needs a CC-1c emitter
 *     before transport has anything to carry).
 *   - The admin dashboard UI is NOT built here (CC-1d).
 */

import {
  TELEMETRY_SCHEMA_VERSION,
  telemetryBatchSchema,
  telemetryEventSchema,
  type AuditEvent,
  type EditorShellState,
  type TelemetryBatch,
  type TelemetryEvent,
  type TelemetryEventKind,
  type TelemetryNavKind,
  type TelemetryParams,
  type TelemetryStatus,
  type TelemetryTone,
} from "@dxf-ai-editor/contracts";
import {
  resolveBackendApiBaseUrl,
  type ViteBackendEnv,
} from "../backend/backendApi";
import {
  BatchedServerChannel,
  createFetchBatchTransport,
  generateClientEventUid,
  type BatchedServerChannelStats,
} from "./batchedServerChannel";
import {
  AuditServerChannel,
  createFetchAuditTransport,
  projectAuditEventToAuditRecord,
  type AuditServerChannelOptions,
} from "./auditServerChannel";

export const TELEMETRY_INGEST_PATH = "/api/v1/telemetry";

export interface TelemetryFlushResult {
  ok: boolean;
  status?: number;
}

/**
 * Pluggable delivery sink. Production wires `createFetchTelemetryTransport`;
 * tests inject a fake that can fail N times, count batches or capture the
 * payload for schema assertions.
 */
export type TelemetryTransport = (
  batch: TelemetryBatch,
) => Promise<TelemetryFlushResult>;

/**
 * Delivery stats for the telemetry channel — structurally the shared
 * `BatchedServerChannelStats` (this channel is a specialization of the generic
 * batched server channel).
 */
export type TelemetryServerChannelStats = BatchedServerChannelStats;

export interface TelemetryServerChannelOptions {
  transport: TelemetryTransport;
  /** Auto-flush once the queue reaches this many events. Default 25. */
  batchSize?: number;
  /** Max events per POST (must be <= contract cap of 500). Default 200. */
  maxChunkSize?: number;
  /** Periodic background flush interval in ms. 0 disables the timer. Default 5000. */
  flushIntervalMs?: number;
  /** Per-chunk retry attempts after the first try. Default 4. */
  maxRetries?: number;
  /** Exponential backoff base in ms (delay = base * 2^attempt). Default 400. */
  baseRetryDelayMs?: number;
  /** Backpressure cap — oldest events are dropped past this. Default 2000. */
  maxQueueSize?: number;
  now?: () => number;
  /** Injectable delay (tests pass a no-op to avoid real timers). */
  delay?: (ms: number) => Promise<void>;
  onError?: (error: unknown) => void;
}

/**
 * Batched, retrying, backpressured server telemetry channel (ТЗ §14). A thin
 * specialization of the shared `BatchedServerChannel`: the reliability
 * machinery (batch / retry / in-flight identity pinning / backpressure /
 * auth-drop / validation-drop) lives in one place; this wrapper supplies only
 * the telemetry validators + wire batch build. The public API
 * (`enqueue`/`flush`/`idle`/`size`/`stats`/`dispose`) is unchanged.
 */
export class TelemetryServerChannel {
  private readonly core: BatchedServerChannel<TelemetryEvent, TelemetryBatch>;

  constructor(options: TelemetryServerChannelOptions) {
    this.core = new BatchedServerChannel<TelemetryEvent, TelemetryBatch>({
      transport: options.transport,
      // Enqueue-time validation: push the schema-PARSED event (never the raw
      // input), so a malformed event is rejected + counted, never queued.
      validateEvent: (event) => {
        const parsed = telemetryEventSchema.safeParse(event);
        return parsed.success ? (parsed.data as TelemetryEvent) : null;
      },
      buildBatch: (events) => ({
        schemaVersion: TELEMETRY_SCHEMA_VERSION,
        events,
      }),
      // Defensive pre-send re-validation (the batch is rebuilt from already-
      // validated events, but a malformed chunk must not wedge the loop). On
      // failure surface the error via onError, then drop the chunk (null).
      validateBatch: (batch) => {
        const validated = telemetryBatchSchema.safeParse(batch);
        if (validated.success) return validated.data as TelemetryBatch;
        options.onError?.(validated.error);
        return null;
      },
      batchSize: options.batchSize,
      maxChunkSize: options.maxChunkSize,
      // Wire contract cap — `telemetryBatchSchema.events` is `.max(500)`.
      hardChunkCap: 500,
      flushIntervalMs: options.flushIntervalMs,
      maxRetries: options.maxRetries,
      baseRetryDelayMs: options.baseRetryDelayMs,
      maxQueueSize: options.maxQueueSize,
      delay: options.delay,
      onError: options.onError,
    });
  }

  enqueue(event: TelemetryEvent): boolean {
    return this.core.enqueue(event);
  }

  flush(): Promise<void> {
    return this.core.flush();
  }

  /** #479 F4 — last-chance unload flush (keepalive, all chunks initiated up front). */
  flushFinal(): Promise<void> {
    return this.core.flushFinal();
  }

  idle(): Promise<void> {
    return this.core.idle();
  }

  size(): number {
    return this.core.size();
  }

  stats(): TelemetryServerChannelStats {
    return this.core.stats();
  }

  dispose(): void {
    this.core.dispose();
  }
}
// ──────────────────────────────────────────────────────────────────────────
// Audit-event → TelemetryEvent projection
// ──────────────────────────────────────────────────────────────────────────

/**
 * View-op command types do NOT mutate geometry. REGEN/REGENALL (CC-1a A1)
 * plus ZOOM/PAN (their CC-1c emitters land later) are classified here so the
 * transport tags them `kind:"view-op"`. Anchored at a token boundary so it
 * never misclassifies a geometry command that merely contains the substring.
 */
const VIEW_OP_TYPE_PATTERN = /(?:^|[/-])(?:regen|regenall|zoom|pan)$/i;

export function classifyTelemetryKind(commandType: string): TelemetryEventKind {
  return VIEW_OP_TYPE_PATTERN.test(commandType.trim()) ? "view-op" : "command";
}

/**
 * CC-1e — resolve the wire `kind` for an audit entry. The non-command
 * interaction family carries its kind in the typed `params.category` (the
 * source of truth — emitters always attach it via `describeUi*Operation`), so
 * recover it there; otherwise fall back to the commandType string pattern
 * (which still classifies command vs view-op, and stays correct for the
 * pre-CC-1c snapshots that carry no `params`).
 */
function classifyAuditKind(audit: AuditEvent): TelemetryEventKind {
  switch (audit.params?.category) {
    case "ui-click":
      return "ui-click";
    case "ui-toggle":
      return "ui-toggle";
    case "ui-nav":
      return "ui-nav";
    default:
      return classifyTelemetryKind(audit.commandType);
  }
}

/** Read a string scalar off the (catchall) ring params, defaulting to "". */
function paramString(
  params: AuditEvent["params"],
  key: string,
): string {
  const value = params?.[key];
  return typeof value === "string" ? value : "";
}

function toneToStatus(tone: TelemetryTone): TelemetryStatus {
  // ТЗ §14 "errors": a FAILED command / parse error carries the `error` tone
  // and must land in the `status:"error"` aggregation bucket (with an
  // `errorMessage`, enforced by the contract refine). `warning` stays
  // `warning`; everything else (info / success) is a successful `ok` outcome.
  if (tone === "error") return "error";
  if (tone === "warning") return "warning";
  return "ok";
}

export interface AuditProjectionOverrides {
  tone?: TelemetryTone;
  status?: TelemetryStatus;
  params?: TelemetryParams;
  durationMs?: number;
  errorMessage?: string;
  timestampMs?: number;
  /**
   * CC-1a idempotency — injectable per-event uid. Production omits it (a fresh
   * collision-resistant uid is minted per projection); tests pass a fixed value
   * to assert retry-stability vs reset-distinctness deterministically.
   */
  clientEventUid?: string;
}

/**
 * Project an in-memory audit ring entry into the typed CAD-core telemetry
 * event. The audit ring only stores the lean payload (id/sequence/
 * commandType/source/actorEmail/summary); the richer ТЗ §14 fields (tone /
 * status / params / timings / error) are supplied by the caller — today the
 * tone is recovered from the command-line transcript, and CC-1c emitters
 * will later attach params/durations/error status at the SITE.
 */
export function projectAuditEventToTelemetry(
  audit: AuditEvent,
  overrides: AuditProjectionOverrides = {},
): TelemetryEvent {
  const tone = overrides.tone ?? "info";
  const status = overrides.status ?? toneToStatus(tone);
  const base = {
    schemaVersion: TELEMETRY_SCHEMA_VERSION,
    id: audit.id,
    sequence: audit.sequence,
    source: audit.source,
    actorEmail: audit.actorEmail,
    summary: audit.summary,
    // CC-1a idempotency — mint the collision-resistant dedupe key HERE, once,
    // as the event object is built (it is never re-minted on a channel retry,
    // so a retry re-POSTs the same uid and the server dedupes it).
    clientEventUid: overrides.clientEventUid ?? generateClientEventUid(),
    tone,
    status,
    timestampMs: overrides.timestampMs ?? Date.now(),
    // ABB-S2-CONTRACTS — carry the originating CHANNEL through the typed-field
    // projection seam. `auditEventSchema.channel` flows into the WIRE shape
    // automatically (`telemetryEventBaseSchema` is `.omit({commandType})`-based,
    // so every other audit field — channel included — is inherited), but this
    // helper builds `base` MANUALLY and would otherwise drop it. Without this a
    // bridge turn audited as `source:'system', channel:'agent-bridge'` would
    // POST with no channel and the server-side history could not distinguish the
    // remote-agent actor from any other `source:'system'` command. Conditional-
    // spread (matching `params`/`durationMs`) so a channel-less audit entry — a
    // pre-slice snapshot, or any lean `pushAuditEvent` payload — never stamps an
    // explicit `channel: undefined` onto the wire object.
    ...(audit.channel !== undefined ? { channel: audit.channel } : {}),
    ...(overrides.params ? { params: overrides.params } : {}),
    ...(overrides.durationMs !== undefined
      ? { durationMs: overrides.durationMs }
      : {}),
    // #353 @codex F1 — a status:"error" event MUST carry a NON-EMPTY
    // errorMessage (contract refine on telemetryEventSchema), else `enqueue`
    // rejects + DROPS it. Default to the audit `summary` (the user-facing
    // failure text, same as the transport's per-event projection) when no
    // explicit override is given, and fall back to a fixed non-empty sentinel
    // if BOTH the override and the summary are empty — so a DIRECT caller of
    // this exported helper can never produce a refine-invalid error event
    // (e.g. an unvalidated AuditEvent with an empty summary).
    ...(status === "error"
      ? {
          errorMessage:
            overrides.errorMessage || audit.summary || "Command failed.",
        }
      : {}),
  };

  const kind = classifyAuditKind(audit);
  switch (kind) {
    case "view-op":
      return { ...base, kind: "view-op", opType: audit.commandType };
    case "ui-click":
      // CC-1e — lift the typed click fields from the ring params onto the wire
      // arm (the §14 "clicks" axis). params is always present for ui-* events.
      return {
        ...base,
        kind: "ui-click",
        target: paramString(audit.params, "target"),
        controlId: paramString(audit.params, "controlId"),
      };
    case "ui-toggle":
      return {
        ...base,
        kind: "ui-toggle",
        setting: paramString(audit.params, "setting"),
        enabled: audit.params?.enabled === true,
      };
    case "ui-nav":
      return {
        ...base,
        kind: "ui-nav",
        navKind: paramString(audit.params, "navKind") as TelemetryNavKind,
        from: paramString(audit.params, "from"),
        to: paramString(audit.params, "to"),
      };
    default:
      return { ...base, kind: "command", commandType: audit.commandType };
  }
}

/**
 * CC-1c — typed→flat WIRE projection. The audit ring carries the typed
 * `AuditOperationParams` payload (discriminants + op-specific scalars). The
 * server wire shape is the flat scalar `TelemetryParams` bag. This is the
 * single boundary where the typed in-code payload is widened to the serialised
 * wire form — a shallow copy, since every field is already a JSON scalar.
 */
export function toWireTelemetryParams(
  params: AuditEvent["params"],
): TelemetryParams | undefined {
  return params ? { ...params } : undefined;
}

// ──────────────────────────────────────────────────────────────────────────
// Editor-store wiring (audit ring → channel)
// ──────────────────────────────────────────────────────────────────────────

export interface AuditTelemetryStoreLike {
  subscribe: (listener: () => void) => () => void;
  /**
   * Gate #4 round-2 (FIX C) — the subscriber also reads the EXPLICIT batch-close
   * discriminator the editor store stamps at each close site
   * (`commit` / `abort` / `discard`). Optional so the test fake store (and any
   * non-editorStore caller) need not provide it — `undefined` means "no explicit
   * signal", and the subscriber treats a close with staged events conservatively
   * (only an explicit `"commit"` flushes; everything else discards).
   */
  getState: () => {
    state: EditorShellState;
    lastBatchClosedBy?: "commit" | "abort" | "discard" | null;
  };
}

/**
 * Subscribe a channel to the editor store's audit ring. On every store
 * update, every audit event with `sequence` greater than the last one seen
 * is projected (tone recovered from the matching transcript entry) and
 * enqueued. Returns an unsubscribe function.
 *
 * Generic over a store-like shape (rather than importing the concrete
 * `useEditorStore`) so the dependency edge points editorStore → here,
 * keeping the module graph acyclic.
 */
export function installAuditTelemetryTransport(params: {
  store: AuditTelemetryStoreLike;
  channel: TelemetryServerChannel;
  now?: () => number;
  /**
   * CC-1b — optional AUDIT-persist tee. Called with the SAME
   * (event, tone, observationTimestampMs, observationActiveSpaceId) tuple the
   * telemetry projection receives, so the audit-events flush rides the identical
   * STAGE-while-open / commit-vs-abort filtering (an aborted batch's events are
   * never persisted). The active space is passed as the value OBSERVED when the
   * event was seen (NOT re-read live at flush) so a staged event committed after
   * a space switch still records the space it was emitted under (#479 F3).
   * Omitted by callers that only want the telemetry stream (and by the test
   * fakes), so the telemetry path is unchanged when absent.
   */
  auditSink?: (
    event: AuditEvent,
    tone: TelemetryTone,
    timestampMs: number,
    activeSpaceId: string | undefined,
  ) => void;
}): () => void {
  const { store, channel } = params;
  const now = params.now ?? (() => Date.now());
  const auditSink = params.auditSink;

  // Seed from the current high-water mark so install does not replay history.
  let lastSeen = store.getState().state.audit.nextSequence - 1;

  // Gate #4 round-1 (P2 fix) — STAGE-WHILE-OPEN buffer. The events observed on
  // each in-batch `set()`, keyed by their (unique, monotonic) audit sequence so
  // the same event seen across multiple `set()`s is captured ONCE. We capture
  // the actual event OBJECT (+ the tone resolved at observation time) the moment
  // it appears, BEFORE the audit ring's newest-24 cap can evict it — so a
  // >24-command committed batch flushes ALL its events, not just the surviving
  // newest 24 that a defer-then-read-the-ring-at-commit approach would see.
  //
  // Gate #4 round-2 (FIX B — telemetryServerChannel.ts:775) — we ALSO record the
  // OBSERVATION-time `timestampMs` (via the channel's injected `now()`) into the
  // staged entry the moment the event is observed. The previous staging saved
  // only `{event, tone}` and re-`now()`-ed at FLUSH (commit time), so every
  // committed in-batch event got a single commit-time timestamp instead of when
  // it was actually observed. Capturing the timestamp at staging time preserves
  // the per-event observation ordering/timing on the wire.
  const staged = new Map<
    number,
    {
      event: AuditEvent;
      tone: TelemetryTone;
      observedTimestampMs: number;
      // #479 F3 — the active space id captured AT OBSERVATION (when the event was
      // staged), NOT re-read at commit. If the user switches space mid-batch, an
      // early staged event committed later must still record the space it was
      // emitted under, or the admin space filter is corrupted.
      observedActiveSpaceId: string | undefined;
    }
  >();

  /**
   * Project ONE audit event (+ its already-resolved tone + the observation-time
   * timestamp) to the wire shape and enqueue it. Shared by the immediate
   * (outside-batch) path and the flush-on-commit path so the projection can
   * never drift between them. The immediate path passes `now()` (observation ==
   * enqueue); the flush-on-commit path passes the STAGED observation timestamp
   * (FIX B) so it is NOT re-`now()`-ed at commit.
   */
  const enqueueEvent = (
    event: AuditEvent,
    tone: TelemetryTone,
    timestampMs: number,
    // #479 F3 — the active space OBSERVED with the event (staged value on the
    // commit path; the live snapshot value on the immediate/fresh path where
    // observation == enqueue). Threaded to the audit tee so it never re-reads a
    // post-switch live snapshot at flush.
    activeSpaceId: string | undefined,
  ): void => {
    // CC-1c — project the typed ring payload the emitter stamped on the entry to
    // the flat WIRE params HERE, at the transport boundary (NOT in the taxonomy).
    // Without this the server channel would only ever see tone + summary, never
    // the structured op params.
    const wireParams = toWireTelemetryParams(event.params);
    channel.enqueue(
      projectAuditEventToTelemetry(event, {
        tone,
        timestampMs,
        ...(wireParams ? { params: wireParams } : {}),
        // ТЗ §14 "errors": a FAILED command / parse error rides the `error`
        // tone. The audit ring stores no separate error field, so the
        // user-facing failure `summary` IS the message the server-side error
        // aggregation groups on (the contract refine REQUIRES it for a
        // `status:"error"` event). Only attached for `error` tone so a normal
        // event never carries a spurious errorMessage.
        ...(tone === "error" ? { errorMessage: event.summary } : {}),
      }),
    );
    // CC-1b — tee the SAME staged/committed event into the audit-persist flush.
    // Placed AFTER the telemetry enqueue so the two streams observe an identical
    // event set; the sink owns its own projection + channel. The
    // observation-time active space (#479 F3) rides along so a post-switch commit
    // still attributes the event to the space it was emitted under.
    auditSink?.(event, tone, timestampMs, activeSpaceId);
  };

  return store.subscribe(() => {
    const snapshot = store.getState();
    const shell = snapshot.state;
    const events = shell.audit.events;
    // CC-1e P2-1 — the non-command interaction family (ui-click / ui-toggle /
    // ui-nav) lives in a SEPARATE bounded queue so it never evicts command /
    // view-op entries out of the visible ring. The transport must observe BOTH
    // queues so those ui/* telemetry events still reach the server channel.
    const uiEvents = shell.audit.uiEvents ?? [];

    // Detect a sequence-space regression: `editorStore.reset()` /
    // new-document rebuilds `audit.nextSequence` back to 1 (and clears the
    // ring). Without this, `lastSeen` stays pinned at the pre-reset
    // high-water mark, every post-reset event has `sequence <= lastSeen`,
    // and telemetry silently dies after any reset. Reseed to 0 so the
    // freshly repopulated ring is re-considered — the ring no longer holds
    // any pre-reset event, so this cannot re-send already-delivered ones. Also
    // drop any stale staged events from a turn that a reset orphaned (their
    // sequence space no longer exists).
    const highWater = shell.audit.nextSequence - 1;
    if (highWater < lastSeen) {
      lastSeen = 0;
      staged.clear();
    }

    // Resolve an event's outcome tone. #353 @codex F7 — recover it from the
    // audit-ring ENTRY FIRST, which now PERSISTS it (`appendAuditEvent`). The
    // ring `tone` is optional, so the matching `tx-${sequence}` transcript row
    // remains a live fallback for entries that carry no persisted tone (e.g. a
    // pre-F7 persisted snapshot), and "info" is the final default.
    const toneById = new Map<string, TelemetryTone>();
    for (const entry of shell.commandLine.transcript) {
      toneById.set(entry.id, entry.tone);
    }
    const resolveTone = (event: AuditEvent): TelemetryTone =>
      event.tone ?? toneById.get(`tx-${event.sequence}`) ?? "info";

    // Gate #4 round-1 (P2 fix) — STAGE while a history batch is OPEN; do NOT
    // enqueue/POST. Forward commands emitted DURING a batch each call
    // `pushAuditEvent` (advancing `audit.nextSequence`), and this subscriber
    // fires on EVERY store `set()`. We capture each newly-appeared in-batch
    // event (sequence > lastSeen, not already staged) into the staged buffer the
    // instant it appears — BEFORE the ring's newest-24 cap can evict it — but we
    // POST nothing yet, because a batch that later ABORTS must stream nothing.
    if (shell.history?.current != null) {
      for (const event of [...events, ...uiEvents]) {
        if (event.sequence > lastSeen && !staged.has(event.sequence)) {
          // FIX B — record the OBSERVATION-time timestamp (channel `now()`) NOW,
          // at staging, NOT at flush. Each staged event keeps the moment it was
          // first observed; the commit flush reuses it verbatim. #479 F3 — capture
          // the active space AT THE SAME MOMENT so a mid-batch space switch cannot
          // rewrite an early event's space to the space active at commit time.
          staged.set(event.sequence, {
            event,
            tone: resolveTone(event),
            observedTimestampMs: now(),
            observedActiveSpaceId: shell.drawing?.activeSpaceId,
          });
        }
      }
      return;
    }

    // The batch is CLOSED (or none was ever open). If we staged events for a
    // just-closed batch, decide flush-vs-discard by the EXPLICIT close
    // discriminator the editor store stamped at the close site (FIX C —
    // telemetryServerChannel.ts:793) — NOT by inferring "commit" from a
    // non-rewound high-water:
    //   - `"commit"`  (history/commit-batch) → FLUSH every staged event
    //     (complete, even for a >24-command batch — we captured the objects +
    //     observation timestamps, not the ring).
    //   - `"abort"` / `"discard"` (history/abort-batch, OR an import / open-new /
    //     INSERT-merge / reset that clears `history.current` WITHOUT a commit) →
    //     DISCARD the STAGED (batch-own) events, but STILL PROJECT any NON-STAGED
    //     fresh audit events appended in the SAME close update (round-3 FIX #2).
    // High-water inference was the round-2 P2: import/open/merge/reset ALSO drive
    // `history.current → null` WITHOUT rewinding the audit ring, so a discarded
    // batch's staged events satisfied `sequence <= highWater` and were POSTED as
    // committed. The explicit signal makes the close kind unambiguous. The fake
    // test store omits the field → `undefined`; we treat any non-`"commit"`
    // close (including `undefined`) as discard, so only a real commit flushes.
    if (staged.size > 0) {
      const committed = snapshot.lastBatchClosedBy === "commit";
      if (committed) {
        // COMMIT: flush every staged event (sorted ascending) with the
        // OBSERVATION-time timestamp captured at staging, never a commit-time
        // `now()` (FIX B).
        const stagedEntries = [...staged.values()].sort(
          (a, b) => a.event.sequence - b.event.sequence,
        );
        staged.clear();
        for (const {
          event,
          tone,
          observedTimestampMs,
          observedActiveSpaceId,
        } of stagedEntries) {
          // #479 F3 — pass the space captured at OBSERVATION, never the live
          // snapshot's `shell.drawing?.activeSpaceId` at this (commit) moment.
          enqueueEvent(event, tone, observedTimestampMs, observedActiveSpaceId);
        }
        // On commit highWater == the last flushed sequence — advance past it so
        // the immediate path never re-considers a staged sequence.
        lastSeen = Math.max(lastSeen, highWater);
        return;
      }

      // ABORT / DISCARD / undefined (round-3 FIX #2 — telemetryServerChannel.ts:855):
      // DISCARD ONLY the STAGED sequences (the batch's own events — the turn
      // never happened on the wire), but STILL PROJECT any NON-STAGED fresh
      // audit events appended in the SAME store update as the close. The round-2
      // discard blanket-advanced `lastSeen` to `highWater` and dropped EVERYTHING
      // up to it — including a legitimate event appended alongside the close
      // (e.g. `importDxfText` → `pushAuditEvent(importHeal.summary)` then
      // `lastBatchClosedBy:"discard"`), which thereby ended up `<= lastSeen` and
      // was never enqueued. Membership in the staged set is by SEQUENCE (the
      // `staged` Map is keyed by sequence), so the staged-vs-fresh distinction is
      // EXACT: a fresh non-staged event has `sequence > lastSeen` AND
      // `!staged.has(sequence)`.
      const stagedSequences = new Set(staged.keys());
      staged.clear();
      const freshNonStaged = [...events, ...uiEvents]
        .filter(
          (event) =>
            event.sequence > lastSeen && !stagedSequences.has(event.sequence),
        )
        .sort((a, b) => a.sequence - b.sequence);
      for (const event of freshNonStaged) {
        // Observation == enqueue for these fresh-on-close events (they were not
        // staged), so stamp `now()` and the LIVE active space (#479 F3) — both
        // are the observation values for this path.
        enqueueEvent(
          event,
          resolveTone(event),
          now(),
          shell.drawing?.activeSpaceId,
        );
      }
      // Advance lastSeen past the WHOLE close update — both the discarded staged
      // sequences (so they are never re-considered) AND the projected fresh ones.
      // `highWater` covers the discarded staged sequences (the ring was NOT
      // rewound on import/open/merge/reset); the projected fresh events are also
      // <= highWater (they were appended via `pushAuditEvent`, advancing
      // nextSequence), so a single `max(lastSeen, highWater)` covers both. (On a
      // ring-rewinding reset the regression reseed above already set lastSeen=0
      // and cleared `staged`, so this branch is not reached for that case.)
      lastSeen = Math.max(lastSeen, highWater);
      return;
    }

    if (events.length === 0 && uiEvents.length === 0) return;

    // Outside any open batch: IMMEDIATE projection (the unchanged path). Both
    // queues are newest-first; merge them, then project the oldest new event
    // first for ordering. The shared `nextSequence` counter stamps a unique,
    // monotonic `sequence` across BOTH queues, so the `sequence > lastSeen`
    // high-water filter de-dupes correctly regardless of which queue an event
    // landed in.
    const fresh = [...events, ...uiEvents]
      .filter((event) => event.sequence > lastSeen)
      .sort((a, b) => a.sequence - b.sequence);

    if (fresh.length === 0) return;

    // Immediate (outside-batch) path: observation == enqueue, so pass `now()` as
    // the observation timestamp (FIX B) and the LIVE active space (#479 F3) — both
    // are the observation values for this path.
    for (const event of fresh)
      enqueueEvent(event, resolveTone(event), now(), shell.drawing?.activeSpaceId);
    // `fresh` is sorted ascending and every entry passed `sequence > lastSeen`,
    // so the last element is the new high-water mark.
    lastSeen = fresh[fresh.length - 1]!.sequence;
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Production transport + default install
// ──────────────────────────────────────────────────────────────────────────

export interface FetchTelemetryTransportOptions {
  /**
   * Backend origin. `undefined` means NO telemetry endpoint is configured
   * (e.g. a test that mocks `resolveBackendApiBaseUrl`, or a deployment with
   * no backend) — only then is a no-op transport returned instead of building
   * a fetch URL. An EMPTY STRING is NOT "no backend": it is the normalized
   * same-origin root (`VITE_API_BASE_URL='/'` → `''`, see
   * `resolveBackendApiBaseUrl`), and the rest of the backend client builds
   * valid same-origin `/api/v1/...` paths from it — so `''` must POST, not
   * drain. Typed `string | undefined` so the "no backend" reality is visible
   * to callers, not hidden behind a cast.
   */
  baseUrl: string | undefined;
  fetchImpl?: typeof fetch;
  path?: string;
  /** Unload-flush hook forwarded to `createFetchBatchTransport` (keepalive POST). */
  keepalive?: () => boolean;
}

/**
 * No-op delivery sink for the "no backend telemetry endpoint configured" case.
 * Reports `ok:true` so the channel treats the batch as delivered and drains it
 * (no retry storm, no unbounded queue growth) — silently dropping telemetry is
 * the correct behaviour when there is nowhere to POST it.
 */
const NO_OP_TELEMETRY_TRANSPORT: TelemetryTransport = () =>
  Promise.resolve({ ok: true });

/**
 * Default delivery sink: a credentialed JSON POST to the Laravel ingest
 * endpoint. Credentials are included so the session cookie (and Laravel's
 * `XSRF-TOKEN`, mirrored into the `X-XSRF-TOKEN` header) authenticate the
 * request the same way the rest of the session-aware API surface does.
 *
 * Only when `baseUrl` is `undefined` is there no endpoint to POST to, so a
 * no-op transport is returned (the `.replace` URL build never runs on
 * `undefined`). An empty-string `baseUrl` is the valid same-origin root and
 * builds a relative `/api/v1/telemetry` POST — it must NOT drain.
 */
export function createFetchTelemetryTransport(
  options: FetchTelemetryTransportOptions,
): TelemetryTransport {
  const { baseUrl } = options;
  // Treat ONLY `undefined` as "no backend". `''` is the same-origin root
  // (normalized `VITE_API_BASE_URL='/'`) — a valid config that must POST. The
  // shared `createFetchBatchTransport` owns the credentialed-POST + XSRF
  // delivery contract (one implementation for telemetry + audit).
  if (baseUrl === undefined) return NO_OP_TELEMETRY_TRANSPORT;

  return createFetchBatchTransport<TelemetryBatch>({
    baseUrl,
    path: options.path ?? TELEMETRY_INGEST_PATH,
    fetchImpl: options.fetchImpl,
    keepalive: options.keepalive,
  });
}

/**
 * Build the production channel (fetch transport, default batching/retry) and
 * wire it to the editor store's audit ring. Called once from editorStore
 * inside the browser guard. Returns the channel + a dispose handle.
 */
export function installDefaultTelemetryServerChannel(
  store: AuditTelemetryStoreLike,
  options: {
    baseUrl?: string;
    channelOptions?: Partial<TelemetryServerChannelOptions>;
    /** CC-1b — tuning overrides for the audit-persist channel (tests inject). */
    auditChannelOptions?: Partial<AuditServerChannelOptions>;
  } = {},
): {
  channel: TelemetryServerChannel;
  auditChannel: AuditServerChannel;
  dispose: () => void;
} {
  let env: ViteBackendEnv | undefined;
  try {
    env = import.meta.env as ViteBackendEnv;
  } catch {
    env = undefined;
  }
  // `resolveBackendApiBaseUrl` is typed `string`, but a test that mocks
  // `backendApi` with bare `vi.fn()`s (App.test.tsx et al.) makes it return
  // `undefined` at module-import time. `createFetchTelemetryTransport` treats
  // ONLY an `undefined` `baseUrl` as "no endpoint" and yields a no-op
  // transport, so install never throws on import (the CC-1c eager-install
  // regression) and never POSTs when no backend is configured. A `''` base URL
  // (same-origin root) is a real backend and POSTs to `/api/v1/telemetry`.
  const baseUrl = options.baseUrl ?? resolveBackendApiBaseUrl(env);

  // #479 F5 — unload flush gate. Queued records below `batchSize` would
  // otherwise wait for the 5 s background timer and be lost when the user closes
  // or navigates away. On `pagehide` / `visibilitychange`→hidden we set this
  // flag and force a flush; the fetch transports read it to issue a
  // `keepalive: true` POST so the request survives document teardown.
  let unloading = false;
  const isUnloading = (): boolean => unloading;

  const channel = new TelemetryServerChannel({
    transport: createFetchTelemetryTransport({ baseUrl, keepalive: isUnloading }),
    ...options.channelOptions,
  });

  // CC-1b — the audit-persist channel flushes the SAME ring into `audit_events`
  // (POST /api/v1/audit). It reuses the telemetry subscriber's abort-filtered
  // ring observation via the `auditSink` tee below, so it never re-subscribes.
  const auditChannel = new AuditServerChannel({
    transport: createFetchAuditTransport({ baseUrl, keepalive: isUnloading }),
    ...options.auditChannelOptions,
  });

  const unsubscribe = installAuditTelemetryTransport({
    store,
    channel,
    auditSink: (event, tone, timestampMs, activeSpaceId) => {
      // #479 F3 — use the active space id OBSERVED with the event (passed by the
      // subscriber: the staged value on a committed batch, the live value on the
      // immediate path), NOT a fresh `store.getState()` read at flush. Reading
      // live here attributed every staged event in a committed batch to the space
      // active AT COMMIT, so a mid-batch MSPACE/PSPACE switch corrupted the admin
      // space filter for the batch's earlier events. It is the fallback
      // `spaceContext` (a per-command `params.spaceId` still wins inside the
      // projection); a `undefined`/empty value degrades to the projection's own
      // `"model"` default.
      auditChannel.enqueue(
        projectAuditEventToAuditRecord(event, {
          tone,
          timestampMs,
          ...(activeSpaceId ? { activeSpaceId } : {}),
        }),
      );
    },
  });

  // Unload-safe flush wiring. `pagehide` is the reliable last-chance event on
  // real navigation/close; `visibilitychange`→hidden is the last reliable event
  // on mobile (tab background / app switch) where `pagehide` may not fire. Both
  // set `unloading` (so the transport upgrades to a keepalive POST) and force a
  // flush of any sub-batch backlog. On return to visible we clear the flag so
  // steady-state POSTs go back to non-keepalive.
  const flushOnHide = (): void => {
    unloading = true;
    // #479 F4 — `flushFinal` (NOT `flush`): initiate a fresh keepalive POST for
    // every queued chunk synchronously, up front, rather than coalescing onto a
    // pre-unload non-keepalive in-flight request (which navigation can cancel) or
    // draining chunks sequentially (which teardown can cut off after the first).
    void channel.flushFinal();
    void auditChannel.flushFinal();
  };
  const onVisibilityChange = (): void => {
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      flushOnHide();
    } else {
      unloading = false;
    }
  };
  const hasWindow =
    typeof window !== "undefined" &&
    typeof window.addEventListener === "function";
  const hasDocument =
    typeof document !== "undefined" &&
    typeof document.addEventListener === "function";
  if (hasWindow) window.addEventListener("pagehide", flushOnHide);
  if (hasDocument) {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  return {
    channel,
    auditChannel,
    dispose: () => {
      if (hasWindow) window.removeEventListener("pagehide", flushOnHide);
      if (hasDocument) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      unsubscribe();
      channel.dispose();
      auditChannel.dispose();
    },
  };
}
